import { useState, type ReactNode } from 'react';
import type { CustomerVolume } from '../types';
import { DailyPagesCard } from './DailyPagesCard';
import type { LinkFocus } from './link';
import { MonthlyRampCard } from './MonthlyRampCard';
import { RangeSwitch } from './RangeSwitch';
import type { RenderBreakdown } from './spans';
import { rangeDays, rangeLabel, ranges, type Range } from './usage-range';
import { WorkHoursCard } from './WorkHoursCard';
import './insights.css';

export interface UsageSectionProps {
  volume: CustomerVolume;
  /** The company's locale, for day and month words. */
  locale: string;
  /** The page's one range, held in the URL by the page. */
  range: Range;
  onRange: (range: Range) => void;
  /**
   * The plan's credits a period, for the line on the credits ramp, and the current period projected
   * to its end at the pace (the account's `credits.projected`), drawn as an outline over it.
   */
  plan: { key: string | null; credits: number; projected?: number | undefined };
  /** Draws the detail behind a clicked or dragged span; without it the columns only explain themselves. */
  renderBreakdown?: RenderBreakdown | undefined;
  /** True while a new range is read: the charts stay, quieter, instead of flashing empty. */
  refreshing?: boolean | undefined;
}

const OPTIONS = ranges.map((value) => ({ value, label: rangeLabel[value] }));

/**
 * The customer page's usage (round 1): one range for every chart, sticky above them; pages over the
 * range, the monthly ramp, and when they work, linked so the column under the pointer lights its
 * weekday and month in the others. Every chart has its tooltip, its detail and its table.
 */
export function UsageSection(props: UsageSectionProps): ReactNode {
  const { volume, locale, range } = props;
  const [focus, setFocus] = useState<LinkFocus>(null);
  const link = { focus, onFocus: setFocus };
  return (
    <section
      className={`ad-vol${props.refreshing ? ' is-refreshing' : ''}`}
      aria-labelledby="ad-vol-title"
    >
      <UsageBar timeZone={volume.timeZone} range={range} onRange={props.onRange} />
      <DailyPagesCard
        key={range}
        days={volume.days}
        range={range}
        timeZone={volume.timeZone}
        locale={locale}
        firstPagesAt={volume.firstPagesAt}
        {...link}
        renderBreakdown={props.renderBreakdown}
      />
      <div className="ad-vol__pair">
        <MonthlyRampCard
          months={volume.months}
          periods={volume.periods}
          today={volume.today}
          locale={locale}
          plan={props.plan}
          {...link}
          renderBreakdown={props.renderBreakdown}
        />
        <WorkHoursCard volume={volume} days={rangeDays[range]} {...link} />
      </div>
    </section>
  );
}

/** The section's name, its zone and the one range every chart below shares; it stays in view while they scroll. */
function UsageBar(props: {
  timeZone: string;
  range: Range;
  onRange: (r: Range) => void;
}): ReactNode {
  return (
    <div className="ad-vol__bar">
      <h2 id="ad-vol-title" className="ad-vol__title">
        Usage
      </h2>
      <span className="ad-small">In their time zone, {props.timeZone}</span>
      <RangeSwitch
        label="Range for every chart"
        options={OPTIONS}
        value={props.range}
        onChange={props.onRange}
      />
    </div>
  );
}
